import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BrainCircuit, CheckCircle2, Loader2, AlertTriangle, ArrowDownLeft, ArrowUpRight,
  Upload, Zap, TrendingUp, XCircle, Bug, FileText, Shield,
} from "lucide-react";

import * as api from "./api";

const VAULT_LABELS = {
  TAX_CUSTODY: "Tax Custody", SUPPLIER_OBLIGATION: "Supplier Obligation",
  CUSTOMER_TRUST: "Customer Trust", INVENTORY_CUSTODY: "Inventory Custody",
  OPERATING_CUSTODY: "Operating Custody", FREE_EQUITY: "Free Equity",
};

/* ═══════════════════════════════════════════
   DEBUG PANEL — always visible, stops guessing
   ═══════════════════════════════════════════ */
function DebugPanel({ batchId, debug, lastError }) {
  const fields = [
    { key: "batch_id",            label: "currentBatchId",   value: batchId || "(none)", mono: true },
    { key: "records_parsed",      label: "recordsParsed",    value: debug?.records_parsed ?? 0 },
    { key: "records_loaded",      label: "recordsLoaded",    value: debug?.records_loaded ?? 0 },
    { key: "records_not_analysed",label: "recordsNotAnalysed", value: debug?.records_not_analysed ?? 0 },
    { key: "records_safe",        label: "recordsSafe",      value: debug?.records_safe ?? 0, colour: "text-emerald-400" },
    { key: "records_needs_review",label: "recordsNeedsReview", value: debug?.records_needs_review ?? 0, colour: "text-amber-400" },
    { key: "records_approved",    label: "recordsApproved",  value: debug?.records_approved ?? 0, colour: "text-cyan-400" },
    { key: "records_committed",   label: "recordsCommitted", value: debug?.records_committed ?? 0, colour: "text-purple-400" },
    { key: "records_applied",     label: "recordsApplied",   value: debug?.records_applied ?? 0, colour: "text-amber-400" },
    { key: "records_failed",      label: "recordsFailed",    value: debug?.records_failed ?? 0, colour: "text-red-400" },
  ];
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="v2-debug-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bug className="w-4 h-4 text-amber-400" />
        <h3 className="text-xs font-barlow font-bold text-amber-400 uppercase tracking-wider">Live Debug Truth — Narrow Path v2</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        {fields.map((f) => (
          <div key={f.key} className="px-3 py-2 rounded-md bg-slate-900/50 border border-slate-700/30" data-testid={`debug-${f.key}`}>
            <div className="text-[9px] text-slate-500 uppercase tracking-wider truncate">{f.label}</div>
            <div className={`font-mono font-bold text-base mt-0.5 ${f.colour || "text-slate-100"} ${f.mono ? "text-[10px] truncate" : ""}`}
              data-testid={`debug-${f.key}-value`}>
              {f.mono ? String(f.value).slice(0, 12) : (f.value ?? 0)}
            </div>
          </div>
        ))}
      </div>
      {lastError && (
        <div className="mt-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-[11px] text-red-300"
          data-testid="debug-last-error">
          <span className="font-bold uppercase text-red-400 mr-2">lastError:</span>{lastError}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */
export default function DataPoolV2Page() {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [batchId, setBatchId] = useState(null);
  const [records, setRecords] = useState([]);
  const [debug, setDebug] = useState(null);
  const [fileResults, setFileResults] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [applyResult, setApplyResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [runSummary, setRunSummary] = useState(null);

  const refresh = useCallback(async (bid) => {
    try {
      const res = await api.getRecords(bid);
      setRecords(res.data.records || []);
      setDebug(res.data.debug || null);
    } catch (e) {
      setLastError(e.response?.data?.detail?.message || e.message);
    }
  }, []);

  const handleFilesSelected = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true); setLastError(null); setApplyResult(null); setRunSummary(null);
    setRecords([]); setFileResults([]); setRejectedFiles([]); setDebug(null);
    try {
      const bres = await api.createBatch();
      const bid = bres.data.batch.id;
      setBatchId(bid);
      const up = await api.uploadCsv(bid, files);
      setFileResults(up.data.file_results || []);
      setRejectedFiles(up.data.rejected_files || []);
      setDebug(up.data.debug || null);
      if (up.data.status === "failed") {
        setLastError(up.data.message || "Upload failed");
      }
      await refresh(bid);
    } catch (e) {
      setLastError(e.response?.data?.detail?.message || e.message || "Upload error");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDiagnose = async () => {
    if (!batchId) return;
    setDiagnosing(true); setLastError(null);
    try {
      const res = await api.diagnose(batchId);
      setRunSummary(res.data.run);
      setDebug(res.data.debug || null);
      await refresh(batchId);
    } catch (e) {
      setLastError(e.response?.data?.detail?.message || e.message);
    } finally { setDiagnosing(false); }
  };

  const handleApprove = async (recordId) => {
    try {
      await api.approveRecord(recordId);
      await refresh(batchId);
    } catch (e) { setLastError(e.response?.data?.detail?.message || e.message); }
  };

  const handleApproveAllSafe = async () => {
    const safe = records.filter((r) => r.status === "safe");
    for (const r of safe) { try { await api.approveRecord(r.id); } catch (_e) {} }
    await refresh(batchId);
  };

  const handleQuickAllocate = async (rec) => {
    const np = rec.normalized_payload || {};
    const target = np.reporting_amount || 0;
    const allocations = [{
      vault: "OPERATING_CUSTODY",
      source_amount: np.source_amount || 0,
      source_currency: np.source_currency || "GBP",
      reporting_amount: target,
      reporting_currency: np.reporting_currency || "GBP",
      reason_code: "manual_default_allocation",
    }];
    try {
      await api.manualAllocate(rec.id, {
        allocations,
        override_reason: "Emergency path default allocation to Operating Custody",
      });
      await refresh(batchId);
    } catch (e) {
      setLastError(e.response?.data?.detail?.message || e.message);
    }
  };

  const handleCommit = async () => {
    if (!batchId) return;
    setCommitting(true); setLastError(null);
    try {
      const res = await api.commit(batchId);
      setDebug(res.data.debug || null);
      await refresh(batchId);
    } catch (e) { setLastError(e.response?.data?.detail?.message || e.message); }
    finally { setCommitting(false); }
  };

  const handleApply = async () => {
    if (!batchId) return;
    setApplying(true); setLastError(null);
    try {
      const res = await api.apply(batchId);
      setApplyResult(res.data);
      setDebug(res.data.debug || null);
      await refresh(batchId);
    } catch (e) { setLastError(e.response?.data?.detail?.message || e.message); }
    finally { setApplying(false); }
  };

  const safeCount = records.filter((r) => r.status === "safe").length;
  const approvedCount = records.filter((r) => r.status === "approved").length;
  const committedCount = records.filter((r) => r.status === "committed").length;
  const canDiagnose = !!batchId && records.some((r) => r.status === "not_analysed") && !diagnosing;
  const canCommit = approvedCount > 0 && !committing;
  const canApply = committedCount > 0 && !applying;

  return (
    <div className="space-y-5" data-testid="datapool-v2-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-barlow font-bold text-2xl text-slate-50">AI Data Pool</h1>
            <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-none font-mono" data-testid="v2-flag-badge">v2 • EMERGENCY NARROW PATH</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">CSV-only. One active batch. Rules → AI → Reconcile → Commit → Apply.</p>
        </div>
        <Button onClick={() => navigate("/dashboard/data-pool")} size="sm" variant="outline" className="border-slate-700 text-slate-400 text-xs" data-testid="v2-back-to-main">
          Back to full Data Pool
        </Button>
      </div>

      {/* DEBUG PANEL — always visible */}
      <DebugPanel batchId={batchId} debug={debug} lastError={lastError} />

      {/* UPLOAD */}
      <Card className="bg-slate-900/30 border-slate-800/50" data-testid="v2-upload-panel">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-slate-800/60 flex items-center justify-center shrink-0">
              {loading ? <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /> : <Upload className="w-6 h-6 text-slate-400" />}
            </div>
            <div className="flex-1">
              <h2 className="text-base font-barlow font-bold text-slate-200">Upload CSV bank statement</h2>
              <p className="text-xs text-slate-500 mt-0.5">CSV only. Maximum one active batch. Other formats are rejected by this path.</p>
              <Badge className="text-[9px] bg-cyan-500/15 text-cyan-400 border-none mt-2 font-mono">CSV</Badge>
            </div>
            <input ref={inputRef} type="file" accept=".csv" multiple
              className="hidden" onChange={(e) => handleFilesSelected(Array.from(e.target.files || []))}
              data-testid="v2-file-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={loading}
              className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="v2-select-csv-btn">
              {loading ? "Uploading..." : "Select CSV"}
            </Button>
          </div>
          {rejectedFiles.length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300" data-testid="v2-rejected-files">
              <span className="font-bold">Rejected:</span>{" "}
              {rejectedFiles.map((rf, i) => <span key={i}>{rf.filename} ({rf.reason}){i < rejectedFiles.length - 1 ? ", " : ""}</span>)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* BATCH + FILES */}
      {batchId && fileResults.length > 0 && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid="v2-batch-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Current batch</span>
            <span className="font-mono text-cyan-400 text-xs" data-testid="v2-batch-id">{batchId.slice(0, 8)}</span>
            <span className="text-xs text-slate-400">{fileResults.length} file(s) · {debug?.records_loaded ?? 0} record(s)</span>
          </div>
          <div className="mt-2 space-y-1 text-xs">
            {fileResults.map((f) => (
              <div key={f.id} className="flex items-center gap-2 text-slate-500" data-testid={`v2-file-row-${f.id}`}>
                <FileText className="w-3 h-3" />
                <span className="text-slate-300">{f.filename}</span>
                <Badge className={`text-[9px] border-none ${f.status === "parsed" ? "bg-emerald-500/15 text-emerald-400" : f.status === "partial" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
                  {f.status}
                </Badge>
                <span>· extracted {f.records_extracted}, normalized {f.records_normalized}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ACTION BAR */}
      {batchId && records.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="v2-action-bar">
          <Button onClick={handleDiagnose} disabled={!canDiagnose}
            className="bg-cyan-600 hover:bg-cyan-500 gap-2" data-testid="v2-diagnose-btn">
            {diagnosing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BrainCircuit className="w-4 h-4" />}
            Run Diagnostic
          </Button>
          {safeCount > 0 && (
            <Button onClick={handleApproveAllSafe} size="sm" className="bg-emerald-600 hover:bg-emerald-500 gap-2 text-xs" data-testid="v2-approve-safe-btn">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve all safe ({safeCount})
            </Button>
          )}
          <Button onClick={handleCommit} disabled={!canCommit} variant="outline"
            className="border-slate-700 text-slate-300 gap-2" data-testid="v2-commit-btn">
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Commit ({approvedCount})
          </Button>
          <Button onClick={handleApply} disabled={!canApply}
            className="bg-amber-600 hover:bg-amber-500 text-slate-900 gap-2" data-testid="v2-apply-btn">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
            Apply to Dashboard ({committedCount})
          </Button>
        </div>
      )}

      {/* DIAGNOSTIC SUMMARY */}
      {runSummary && (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-3 text-xs flex items-center gap-4 flex-wrap" data-testid="v2-run-summary">
          <BrainCircuit className="w-4 h-4 text-cyan-400" />
          <span className="text-cyan-400 font-semibold">Diagnostic {runSummary.run_status}</span>
          <span className="text-slate-400">rule {runSummary.records_rule_classified} · ai {runSummary.records_ai_classified}</span>
          <span className="text-emerald-400">safe {runSummary.records_safe}</span>
          <span className="text-amber-400">review {runSummary.records_needs_review}</span>
          {runSummary.records_failed > 0 && <span className="text-red-400">failed {runSummary.records_failed}</span>}
          <span className="text-slate-500">· {runSummary.message}</span>
        </div>
      )}

      {/* APPLY RESULT */}
      {applyResult && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4" data-testid="v2-apply-result">
          <div className="flex items-center gap-3 flex-wrap">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-barlow font-bold text-amber-400">Dashboard Updated</h3>
            <span className="text-xs text-slate-400">
              · {applyResult.committed_items_applied} items applied · {applyResult.ledger_entries_created} ledger entries
            </span>
          </div>
          {applyResult.financial_reality?.headline && (
            <p className="text-sm text-slate-200 mt-2 font-semibold" data-testid="v2-headline">{applyResult.financial_reality.headline}</p>
          )}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {Object.entries(applyResult.vault_totals || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between px-3 py-1.5 rounded-md bg-slate-800/30">
                <span className="text-slate-400">{VAULT_LABELS[k] || k}</span>
                <span className="font-mono text-slate-200">{Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate("/dashboard")} size="sm" className="mt-3 bg-cyan-600 hover:bg-cyan-500 text-xs" data-testid="v2-goto-dashboard">
            Open Dashboard
          </Button>
        </div>
      )}

      {/* RECORD LIST */}
      {batchId && records.length > 0 && (
        <div className="space-y-2" data-testid="v2-record-list">
          {records.map((r) => {
            const np = r.normalized_payload || {};
            const ai = r.ai_result;
            const isInflow = np.direction === "inflow";
            const committed = r.status === "committed";
            return (
              <div key={r.id} className={`rounded-xl border p-3 flex items-center gap-3 ${
                r.status === "safe" ? "border-emerald-500/20 bg-emerald-500/[0.03]" :
                r.status === "needs_review" ? "border-amber-500/20 bg-amber-500/[0.03]" :
                r.status === "approved" ? "border-cyan-500/20 bg-cyan-500/[0.03]" :
                committed ? "border-purple-500/20 bg-purple-500/[0.03]" :
                "border-slate-800/40 bg-slate-900/30"
              }`} data-testid={`v2-record-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="text-[9px] bg-slate-700/40 text-slate-300 border-none" data-testid={`v2-record-status-${r.id}`}>
                      {r.status}
                    </Badge>
                    <Badge className={`text-[9px] ${isInflow ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"} border-none gap-0.5`}>
                      {isInflow ? <ArrowDownLeft className="w-2.5 h-2.5" /> : <ArrowUpRight className="w-2.5 h-2.5" />}
                      {isInflow ? "in" : "out"}
                    </Badge>
                    <span className={`text-sm font-mono font-bold ${isInflow ? "text-emerald-400" : "text-red-400"}`}>
                      {isInflow ? "+" : "-"}{np.source_currency} {Number(np.source_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="text-xs text-slate-300 mt-0.5">{np.description || "(no description)"}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{np.counterparty || "Unknown"}</span>
                    {np.date && <span>{String(np.date).slice(0, 10)}</span>}
                    {ai && <span className="text-cyan-400">· {ai.recommended_kind} ({Math.round((ai.confidence || 0) * 100)}%)</span>}
                  </div>
                </div>
                {!committed && (
                  <div className="flex items-center gap-1 shrink-0">
                    {r.status === "safe" && (
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" onClick={() => handleApprove(r.id)}
                        data-testid={`v2-approve-${r.id}`}>Approve</Button>
                    )}
                    {(r.status === "needs_review" || r.status === "not_analysed") && (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500/30 text-amber-400"
                        onClick={() => handleQuickAllocate(r)} data-testid={`v2-allocate-${r.id}`}>
                        Allocate to Operating
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* EMPTY */}
      {!batchId && !loading && (
        <div className="rounded-xl border border-dashed border-slate-800/40 bg-slate-900/20 p-10 text-center" data-testid="v2-empty-state">
          <Shield className="w-10 h-10 text-slate-700 mx-auto mb-2" />
          <p className="text-sm text-slate-300 font-semibold">No active batch yet</p>
          <p className="text-xs text-slate-500 mt-1">Upload a CSV to start the narrow-path flow.</p>
        </div>
      )}
    </div>
  );
}
